import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Download, Edit, Trash2, Calendar, CheckCircle, 
  FileText, Mail, Receipt, Tag, Upload, File, Users, Building2, 
  Save, X, Loader2, Printer, Search, UserPlus, User, Briefcase, AlertTriangle,
  Shield, Key, Send, ChevronLeft, ChevronRight, ChevronDown, Folder, FolderOpen,
  Sparkles, FileArchive, Brain
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { expedientesAPI, contactosAPI, participantesAPI, configuracionAPI, plantillasWordAPI, procedimientosAPI } from '../api';
import { ProcedimientosJudiciales } from '../components/ProcedimientosJudiciales';
import { toast } from '../hooks/use-toast';
import { EncargarIADialog } from '../components/EncargarIADialog';
import AdjuntosSection from '../components/AdjuntosSection';
import ActuacionesSection from '../components/ActuacionesSection';
import FirmasExpedientePanel from '../components/FirmasExpedientePanel';
import TareasSection from '../components/TareasSection';
import NotasSection from '../components/NotasSection';
import EventosSection from '../components/EventosSection';
import FloatingActionButton from '../components/FloatingActionButton';
import PagosSection from '../components/PagosSection';
import PagoTasasSection from '../components/PagoTasasSection';
import AnalisisIAExpediente from '../components/AnalisisIAExpediente';
import NuevaActuacionIA from '../components/NuevaActuacionIA';
import { ContactoWizard } from './ContactosNew';
import ClausulasSection from '../components/ClausulasSection';
import ExpedientesRelacionadosCard from "./ExpedientesRelacionadosCard";
import { obtenerToken } from '../lib/auth';

// Roles disponibles para participantes
const ROLES_PARTICIPANTE = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'potencial_cliente', label: 'Potencial cliente' },
  { value: 'organismo_asignado', label: 'Organismo asignado' },
  { value: 'abogado_propio', label: 'Abogado propio' },
  { value: 'abogado_colaborador', label: 'Abogado colaborador' },
  { value: 'abogado_contrario', label: 'Abogado contrario' },
  { value: 'procurador_propio', label: 'Procurador propio' },
  { value: 'procurador_contrario', label: 'Procurador contrario' },
  { value: 'parte_contraria', label: 'Parte contraria' },
  { value: 'perito', label: 'Perito' },
  { value: 'testigo_propio', label: 'Testigo propio' },
  { value: 'testigo_parte_contraria', label: 'Testigo de la parte contraria' },
  { value: 'colaborador', label: 'Colaborador' },
  { value: 'asesor', label: 'Asesor' },
  { value: 'gestor', label: 'Gestor' },
  { value: 'intermediario', label: 'Intermediario' },
  { value: 'otorgante', label: 'Otorgante' },
  { value: 'proveedor', label: 'Proveedor' },
  { value: 'otro', label: 'Otro' }
];

// Roles permitidos cuando el participante es un ORGANISMO.
// Por defecto "organismo_asignado" (el juzgado del caso). "cliente" permite
// que un organismo (p.ej. un ayuntamiento) figure como cliente; "parte_contraria"
// se usa cuando un organismo público es la parte demandada.
const ROLES_ORGANISMO = ['organismo_asignado', 'cliente', 'parte_contraria'];

const ExpedienteDetalle = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin'; console.log('DEBUG ExpedienteDetalle - user:', user, 'rol:', user?.rol, 'isAdmin:', isAdmin);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determinar destino del botón Atrás según origen
  const fromParam = searchParams.get('from');
  const getBackDestination = () => {
    if (fromParam === 'extrajudicial') {
      return '/expedientes?tab=extrajudicial';
    }
    return '/expedientes';
  };
  
  const [expediente, setExpediente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fasesConfig, setFasesConfig] = useState(['Inicio', 'Demanda', 'Contestación', 'Audiencia Previa', 'Pruebas', 'Sentencia', 'Recurso', 'Ejecución']);
  const [saving, setSaving] = useState(false);
  
  // Diálogos
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isContactoDialogOpen, setIsContactoDialogOpen] = useState(false);
  const [isOrganismoDialogOpen, setIsOrganismoDialogOpen] = useState(false);
  const [isActuacionDialogOpen, setIsActuacionDialogOpen] = useState(false);
  const [isIADialogOpen, setIsIADialogOpen] = useState(false);
  const [isContactoWizardOpen, setIsContactoWizardOpen] = useState(false);
  const [isTareaDialogOpen, setIsTareaDialogOpen] = useState(false);
  const [isNotaDialogOpen, setIsNotaDialogOpen] = useState(false);
  
  // Estados de edición
  const [editForm, setEditForm] = useState({});
  const [contactoForm, setContactoForm] = useState({
    tipo: 'cliente',
    nombre: '',
    nif_cif: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    codigo_postal: '',
    provincia: '',
    notas: ''
  });
  const [organismoForm, setOrganismoForm] = useState({
    nombre: '',
    tipo_organismo: 'juzgado',
    direccion: '',
    telefono: '',
    email: '',
    localidad: '',
    provincia: ''
  });
  
  // Estados locales para secciones
  const [actuaciones, setActuaciones] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [notas, setNotas] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [eventos, setEventos] = useState([]);
  
  // Navegación entre expedientes
  const [navegacion, setNavegacion] = useState({ anterior: null, siguiente: null });
  
  // Referencias para secciones (para scroll del FAB)
  const actuacionesRef = React.useRef(null);
  const tareasRef = React.useRef(null);
  const notasRef = React.useRef(null);
  const eventosRef = React.useRef(null);
  const datosRef = React.useRef(null);
  const [openSections, setOpenSections] = React.useState(["datos", "relacionados", "actuaciones", "analisis-ia", "pagos", "participantes", "eventos", "adjuntos"]);
  const [auditandoIA, setAuditandoIA] = useState(false);
  
  // Participantes
  const [participantes, setParticipantes] = useState([]);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactoForm, setNewContactoForm] = useState({
    nombre: '',
    nif_cif: '',
    email: '',
    telefono: '',
    direccion: '',
    tipo: 'cliente'
  });
  const [contactosDisponibles, setContactosDisponibles] = useState([]);
  const [empresasDisponibles, setEmpresasDisponibles] = useState([]);
  const [organismosDisponibles, setOrganismosDisponibles] = useState([]);
  const [tiposProcedimiento, setTiposProcedimiento] = useState(['Ordinario', 'Verbal', 'Monitorio', 'Ejecutivo', 'General']);
  const [searchContacto, setSearchContacto] = useState('');
  const [selectedContacto, setSelectedContacto] = useState(null);
  const [selectedRol, setSelectedRol] = useState('cliente');
  const [tipoParticipante, setTipoParticipante] = useState('contacto');
  const [loadingContactos, setLoadingContactos] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [editTab, setEditTab] = useState('datos');

  // Generación de escritos Word
  const [isGenerarEscritoOpen, setIsGenerarEscritoOpen] = useState(false);
  const [plantillasWord, setPlantillasWord] = useState([]);
  const [carpetasWord, setCarpetasWord] = useState([]);
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  const [previewDatos, setPreviewDatos] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);
  const [busquedaPlantilla, setBusquedaPlantilla] = useState('');
  const [expandedFoldersEscrito, setExpandedFoldersEscrito] = useState({});
  const [procedimientosEscrito, setProcedimientosEscrito] = useState([]);
  const [selectedProcedimientoId, setSelectedProcedimientoId] = useState(null);

  // Cargar navegación
  const loadNavegacion = async () => {
    try {
      const response = await expedientesAPI.getNavegacion(id);
      setNavegacion({
        anterior: response.data.anterior,
        siguiente: response.data.siguiente
      });
    } catch (error) {
      console.error('Error cargando navegación:', error);
    }
  };

  useEffect(() => {
    loadExpediente();
    loadParticipantes();
    loadNavegacion();
    configuracionAPI.getItems('tipos-procedimiento')
      .then(res => {
        const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        if (items.length > 0) setTiposProcedimiento(items.map(i => i.nombre));
      })
      .catch(() => {});
  }, [id]);

  // Abrir diálogo de edición si viene con ?edit=true
  useEffect(() => {
    if (searchParams.get('edit') === 'true' && expediente) {
      setIsEditDialogOpen(true);
      // Limpiar el parámetro de la URL
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [expediente, searchParams, setSearchParams]);

  // Cargar fases desde configuración
  useEffect(() => {
    const loadFases = async () => {
      try {
        const res = await configuracionAPI.getItems('fases-expediente');
        if (res.data && res.data.length > 0) {
          setFasesConfig(res.data.map(f => f.nombre));
        }
      } catch (error) {
        console.log('Usando fases por defecto');
      }
    };
    loadFases();
  }, []);

  const loadExpediente = async () => {
    try {
      setLoading(true);
      const response = await expedientesAPI.getById(id);
      const exp = response.data;
      setExpediente(exp);
      setEditForm({
        numero_expediente: exp.numero_expediente || '',
        expediente_judicial: exp.expediente_judicial || '',
        nig: exp.nig || '',
        referencia_extrajudicial: exp.referencia_extrajudicial || '',
        titulo: exp.titulo || '',
        serie: exp.serie || '',
        codigo: exp.codigo || '',
        descripcion: exp.descripcion || '',
        tipo: exp.tipo || '',
        tipo_procedimiento: exp.tipo_procedimiento || '',
        rama: exp.rama || '',
        clase: exp.clase || '',
        fase: exp.fase || '',
        estado: exp.estado || 'activo',
        juzgado: exp.juzgado || '',
        juzgado_id: exp.juzgado_id || null,
        procurador: exp.procurador || '',
        contrario: exp.contrario || '',
        cliente_id: exp.cliente_id || '',
        valor_economico: exp.valor_economico || 0,
        fecha_inicio: exp.fecha_inicio?.split('T')[0] || '',
        observaciones: exp.observaciones || ''
      });
      
      // Cargar actuaciones por separado
      try {
        const actResponse = await expedientesAPI.getActuaciones(id);
        // Mapear descripcion a observaciones y añadir titulo desde tipo si no existe
        const mappedActuaciones = actResponse.data.map(act => ({
          ...act,
          titulo: act.titulo || act.tipo || 'Actuación',
          observaciones: act.observaciones || act.descripcion || ''
        }));
        setActuaciones(mappedActuaciones);
      } catch (err) {
        console.log('No se pudieron cargar actuaciones:', err);
        setActuaciones([]);
      }
      
      setTareas(exp.tareas || []);
      setNotas(exp.notas || []);
      // Cargar facturas asociadas al expediente desde endpoint dedicado
      try {
        const facturasRes = await expedientesAPI.getFacturas(id);
        setFacturas(Array.isArray(facturasRes.data) ? facturasRes.data : []);
      } catch (err) {
        console.log('No se pudieron cargar facturas:', err);
        setFacturas([]);
      }
    } catch (error) {
      console.error('Error cargando expediente:', error);
      toast({ title: "Error", description: "No se pudo cargar el expediente", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadParticipantes = async () => {
    try {
      const response = await participantesAPI.getByExpediente(id);
      setParticipantes(response.data);
    } catch (error) {
      console.error('Error cargando participantes:', error);
    }
  };

  const loadContactosDisponibles = async (searchTerm = '') => {
    setLoadingContactos(true);
    try {
      const baseParams = { 
        page_size: 100,
        search: searchTerm || undefined
      };
      
      // Ejecutar las 3 búsquedas en paralelo por tipo de entidad
      const [personasRes, empresasRes, organismosRes] = await Promise.all([
        contactosAPI.getAll({ ...baseParams, tipo_entidad: 'persona' }),
        contactosAPI.getAll({ ...baseParams, tipo_entidad: 'empresa' }),
        contactosAPI.getAll({ ...baseParams, tipo_entidad: 'organismo' })
      ]);
      
      setContactosDisponibles(personasRes.data?.data || personasRes.data || []);
      setEmpresasDisponibles(empresasRes.data?.data || empresasRes.data || []);
      setOrganismosDisponibles(organismosRes.data?.data || organismosRes.data || []);
    } catch (error) {
      console.error('Error cargando contactos:', error);
    } finally {
      setLoadingContactos(false);
    }
  };

  // Buscar contactos cuando el usuario escribe (con debounce)
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (searchContacto.length >= 2) {
      const timeout = setTimeout(() => {
        loadContactosDisponibles(searchContacto);
      }, 300);
      setSearchTimeout(timeout);
    } else if (searchContacto.length === 0) {
      // Si no hay búsqueda, cargar los primeros 50
      loadContactosDisponibles();
    }
    
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchContacto]);

  // Los contactos ya vienen filtrados de la API
  const filteredContactos = contactosDisponibles;

  const filteredOrganismos = organismosDisponibles;
  const filteredEmpresas = empresasDisponibles;

  // Agrupar participantes por rol
  const participantesPorRol = participantes.reduce((acc, p) => {
    const rol = p.rol_display || p.rol;
    if (!acc[rol]) acc[rol] = [];
    acc[rol].push(p);
    return acc;
  }, {});

  const handleAddParticipante = async () => {
    if (!selectedContacto) {
      toast({ title: "Error", description: "Selecciona un contacto o organismo", variant: "destructive" });
      return;
    }
    
    setSaving(true);
    try {
      await participantesAPI.add({
        expediente_id: parseInt(id),
        contacto_id: tipoParticipante !== 'organismo' ? selectedContacto.id : null,
        organismo_id: tipoParticipante === 'organismo' ? selectedContacto.id : null,
        tipo_participante: tipoParticipante === 'empresa' ? 'contacto' : tipoParticipante,
        rol: selectedRol
      });
      
      toast({ title: "Participante añadido", description: `${selectedContacto.nombre} se ha añadido como ${ROLES_PARTICIPANTE.find(r => r.value === selectedRol)?.label}` });
      setSelectedContacto(null);
      setSearchContacto('');
      loadParticipantes();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo añadir el participante", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveParticipante = async (participanteId) => {
    try {
      await participantesAPI.remove(participanteId);
      toast({ title: "Participante eliminado" });
      loadParticipantes();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar el participante", variant: "destructive" });
    }
  };

  const handleEditExpediente = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Transform data to handle empty strings for integer fields
      const transformedData = {
        ...editForm,
        cliente_id: editForm.cliente_id === '' ? null : editForm.cliente_id,
        juzgado_id: editForm.juzgado_id === '' ? null : editForm.juzgado_id,
        valor_economico: editForm.valor_economico === '' ? 0 : parseFloat(editForm.valor_economico) || 0
      };
      
      await expedientesAPI.update(id, transformedData);
      toast({ title: "Expediente actualizado", description: "Los cambios se han guardado correctamente" });
      setIsEditDialogOpen(false);
      loadExpediente();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo actualizar el expediente", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateContacto = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await contactosAPI.create({
        nombre: contactoForm.nombre,
        tipo: contactoForm.tipo,
        nif_cif: contactoForm.nif_cif,
        email: contactoForm.email,
        telefono: contactoForm.telefono,
        direccion: contactoForm.direccion,
        ciudad: contactoForm.ciudad,
        codigo_postal: contactoForm.codigo_postal,
        provincia: contactoForm.provincia,
        notas: contactoForm.notas
      });
      toast({ title: "Contacto creado", description: `${contactoForm.nombre} se ha añadido a la base de datos` });
      setIsContactoDialogOpen(false);
      setContactoForm({ tipo: 'cliente', nombre: '', nif_cif: '', email: '', telefono: '', direccion: '', ciudad: '', codigo_postal: '', provincia: '', notas: '' });
      loadContactosDisponibles();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo crear el contacto", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOrganismo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await contactosAPI.create({
        ...organismoForm,
        tipo_entidad: 'organismo', tipo: 'organismo',
        nombre: organismoForm.nombre,
        expediente_id: id
      });
      toast({ title: "Organismo creado", description: `${organismoForm.nombre} se ha añadido a la base de datos` });
      setIsOrganismoDialogOpen(false);
      setOrganismoForm({ nombre: '', tipo_organismo: 'juzgado', direccion: '', telefono: '', email: '', localidad: '', provincia: '' });
      loadContactosDisponibles();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo crear el organismo", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Crear contacto nuevo y añadirlo como participante
  const handleCreateContactoAndAddParticipante = async () => {
    if (!newContactoForm.nombre.trim()) {
      toast({ title: "Error", description: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    if (!selectedRol) {
      toast({ title: "Error", description: "Selecciona un rol para el participante", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      // 1. Crear el contacto
      const contactoResponse = await contactosAPI.create({
        nombre: newContactoForm.nombre,
        tipo: newContactoForm.tipo || 'cliente',
        nif_cif: newContactoForm.nif_cif || '',
        email: newContactoForm.email || '',
        telefono: newContactoForm.telefono || '',
        direccion: newContactoForm.direccion || ''
      });

      const nuevoContactoId = contactoResponse.data.id;

      // 2. Añadirlo como participante al expediente
      await participantesAPI.create({
        expediente_id: parseInt(id),
        contacto_id: nuevoContactoId,
        rol: selectedRol,
        tipo_participante: 'contacto'
      });

      toast({ 
        title: "Contacto creado y añadido", 
        description: `${newContactoForm.nombre} se ha creado y añadido como ${ROLES_PARTICIPANTE.find(r => r.value === selectedRol)?.label}` 
      });

      // 3. Limpiar formulario y recargar datos
      setNewContactoForm({ nombre: '', nif_cif: '', email: '', telefono: '', direccion: '', tipo: 'cliente' });
      setShowNewContactForm(false);
      loadContactosDisponibles();
      loadParticipantes();
    } catch (error) {
      console.error('Error creando contacto:', error);
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo crear el contacto", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const [generandoIter, setGenerandoIter] = useState(false);

  const handleGenerarIterProcesal = async () => {
    if (generandoIter) return;
    setGenerandoIter(true);
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const token = obtenerToken();
    try {
      toast({
        title: 'Análisis del iter procesal',
        description: 'Lanzando análisis en segundo plano. Puedes seguir usando la app; te avisaremos cuando termine.'
      });

      // 1) Lanza el job (no espera)
      const resp = await fetch(
        `${apiBase}/api/expedientes/${id}/analisis-iter-procesal`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`HTTP ${resp.status}: ${txt}`);
      }
      const data = await resp.json();
      const jobId = data.job_id;
      if (!jobId) throw new Error('No se recibió job_id del servidor');

      // 2) Polling cada 4 s (timeout ~10 min)
      const maxAttempts = 150;
      let attempts = 0;
      let lastMsg = '';
      const poll = async () => {
        attempts += 1;
        try {
          const r = await fetch(
            `${apiBase}/api/expedientes/jobs-ia/${jobId}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const j = await r.json();
          if (j.mensaje && j.mensaje !== lastMsg) {
            lastMsg = j.mensaje;
            toast({ title: `Análisis iter procesal (${j.progreso || 0}%)`, description: j.mensaje });
          }
          if (j.estado === 'done') {
            // Descarga automática del PDF a través del adjunto creado
            if (j.adjunto_id) {
              const dl = await fetch(
                `${apiBase}/api/adjuntos/download/${j.adjunto_id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
              );
              if (dl.ok) {
                const blob = await dl.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const nombre = j.resultado?.nombre_archivo || `iter-procesal_${id}.pdf`;
                a.download = nombre;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              }
            }
            toast({ title: 'Informe generado', description: 'El PDF se ha descargado y se ha creado una actuación con el adjunto.' });
            setGenerandoIter(false);
            try { await loadExpediente(); } catch (e) { /* noop */ }
            return;
          }
          if (j.estado === 'error') {
            toast({
              title: 'Error al generar el informe',
              description: j.error || 'El análisis falló. Revisa los logs del servidor.',
              variant: 'destructive'
            });
            setGenerandoIter(false);
            return;
          }
          if (attempts >= maxAttempts) {
            toast({
              title: 'Tiempo de espera agotado',
              description: 'El análisis sigue en curso; el informe aparecerá en actuaciones cuando termine.',
            });
            setGenerandoIter(false);
            return;
          }
          setTimeout(poll, 4000);
        } catch (e) {
          console.error('Polling error:', e);
          if (attempts >= maxAttempts) {
            setGenerandoIter(false);
            return;
          }
          setTimeout(poll, 6000);
        }
      };
      setTimeout(poll, 3000);
    } catch (error) {
      console.error('Error iter procesal:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo lanzar el análisis',
        variant: 'destructive'
      });
      setGenerandoIter(false);
    }
  };

  const [downloadingZip, setDownloadingZip] = useState(false);

  const handleDownloadAdjuntosZip = async (format) => {
    setDownloadingZip(true);
    try {
      toast({
        title: format === 'markdown' ? 'Convirtiendo a Markdown' : 'Empaquetando archivos',
        description: 'Descargando todos los adjuntos del expediente...'
      });
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/expedientes/${id}/adjuntos/zip?format=${format}`,
        {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${obtenerToken()}` }
        }
      );
      if (response.status === 404) {
        toast({ title: 'Sin adjuntos', description: 'Este expediente no tiene archivos adjuntos.', variant: 'destructive' });
        return;
      }
      if (!response.ok) {
        const txt = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}: ${txt}`);
      }
      const incluidos = response.headers.get('X-Files-Included');
      const fallidos = response.headers.get('X-Files-Failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `expediente-${id}-${format}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: 'Descarga lista',
        description: `${incluidos || 0} archivos incluidos${fallidos && fallidos !== '0' ? `, ${fallidos} no se pudieron descargar` : ''}.`
      });
    } catch (error) {
      console.error('Error descargando ZIP:', error);
      toast({ title: 'Error', description: 'No se pudieron descargar los adjuntos', variant: 'destructive' });
    } finally {
      setDownloadingZip(false);
    }
  };


  const handleDownloadReport = async () => {
    toast({ title: "Generando informe", description: "El documento Word se está preparando..." });
    
    try {
      // Usar el endpoint del backend para generar un documento Word real
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expedientes/${id}/documento`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${obtenerToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al generar el documento');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Expediente_${expediente?.numero_expediente || id}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: "Informe descargado", description: "El documento Word se ha generado correctamente" });
    } catch (error) {
      console.error('Error descargando documento:', error);
      toast({ title: "Error", description: "No se pudo generar el informe", variant: "destructive" });
    }
  };

  // ===== GENERAR ESCRITO WORD =====
  const openGenerarEscrito = async () => {
    setIsGenerarEscritoOpen(true);
    setSelectedPlantilla(null);
    setPreviewDatos(null);
    setBusquedaPlantilla('');
    setProcedimientosEscrito([]);
    setSelectedProcedimientoId(null);
    setLoadingPlantillas(true);
    try {
      const [plantillasRes, carpetasRes, procsRes] = await Promise.all([
        plantillasWordAPI.getAll({ activo: true }),
        plantillasWordAPI.getCarpetas(),
        procedimientosAPI.list(id).catch(() => ({ data: [] }))
      ]);
      setPlantillasWord(plantillasRes.data);
      setCarpetasWord(carpetasRes.data);
      const procs = Array.isArray(procsRes.data) ? procsRes.data : (procsRes.data?.procedimientos || []);
      setProcedimientosEscrito(procs);
      const primerProcId = procs.length > 0 ? procs[0].id : null;
      setSelectedProcedimientoId(primerProcId);
      const previewRes = await plantillasWordAPI.previewDatos(id, primerProcId);
      setPreviewDatos(previewRes.data);
      // Carpetas cerradas por defecto - el usuario abre la que necesite
      setExpandedFoldersEscrito({});
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      toast({ title: "Error", description: "No se pudieron cargar las plantillas Word", variant: "destructive" });
    } finally {
      setLoadingPlantillas(false);
    }
  };

  const cambiarProcedimientoEscrito = async (procIdStr) => {
    const pid = parseInt(procIdStr, 10);
    setSelectedProcedimientoId(Number.isNaN(pid) ? null : pid);
    try {
      const previewRes = await plantillasWordAPI.previewDatos(id, Number.isNaN(pid) ? null : pid);
      setPreviewDatos(previewRes.data);
    } catch (error) {
      console.error('Error actualizando preview de procedimiento:', error);
    }
  };

  const handleGenerarEscrito = async () => {
    if (!selectedPlantilla) {
      toast({ title: "Selecciona una plantilla", variant: "destructive" });
      return;
    }
    try {
      setGenerando(true);
      const response = await plantillasWordAPI.generar(selectedPlantilla.id, id, selectedProcedimientoId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']
        ?.split('filename=')[1]?.replace(/"/g, '')
        || `${selectedPlantilla.nombre}_${expediente?.numero_expediente || id}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: "Escrito generado", description: "El documento Word se ha descargado correctamente" });
      setIsGenerarEscritoOpen(false);
    } catch (error) {
      console.error('Error generando escrito:', error);
      toast({ title: "Error", description: "No se pudo generar el escrito", variant: "destructive" });
    } finally {
      setGenerando(false);
    }
  };

  const handleCreateActuacion = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newActuacion = {
      id: String(actuaciones.length + 1),
      titulo: formData.get('titulo'),
      fecha: formData.get('fecha') || new Date().toISOString().split('T')[0],
      observaciones: formData.get('observaciones'),
      duracion: formData.get('duracion') || '00:00:00',
    };
    setActuaciones([...actuaciones, newActuacion]);
    setIsActuacionDialogOpen(false);
    toast({ title: 'Actuación creada' });
  };

  // Nueva función para crear actuación con API real
  const handleCreateActuacionNew = async (data) => {
    try {
      const response = await expedientesAPI.createActuacion(id, {
        titulo: data.titulo,
        fecha: data.fecha || new Date().toISOString().split('T')[0],
        tipo: 'general',
        descripcion: data.observaciones || '',
        duracion: data.duracion
      });
      toast({ title: 'Actuación creada' });
      return response.data; // Devuelve la actuación con su ID
    } catch (error) {
      console.error('Error creando actuación:', error);
      throw error;
    }
  };

  const handleCreateTarea = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newTarea = {
      id: String(tareas.length + 1),
      titulo: formData.get('titulo'),
      descripcion: formData.get('descripcion'),
      fechaFin: formData.get('fechaFin'),
      estado: formData.get('estado'),
      prioridad: formData.get('prioridad'),
    };
    setTareas([...tareas, newTarea]);
    setIsTareaDialogOpen(false);
    toast({ title: 'Tarea creada' });
  };

  const handleCreateNota = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newNota = {
      id: String(notas.length + 1),
      titulo: formData.get('titulo'),
      descripcion: formData.get('descripcion'),
      fecha: new Date().toISOString().split('T')[0],
      etiquetas: formData.get('etiquetas')?.split(',') || []
    };
    setNotas([...notas, newNota]);
    setIsNotaDialogOpen(false);
    toast({ title: 'Nota creada' });
  };

  const handleAuditarIA = async () => {
    if (!window.confirm('¿Auditar este expediente con IA? El resultado aparecerá en "Auditorías" y como una actuación del expediente.')) return;
    setAuditandoIA(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auditorias-procesales/expediente/${id}/auditar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${obtenerToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error('Error al lanzar la auditoría');
      toast({ title: 'Auditoría lanzada', description: 'Se procesa en segundo plano. Verás el resultado en Auditorías y en una nueva actuación.' });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo lanzar la auditoría', variant: 'destructive' });
    } finally {
      setAuditandoIA(false);
    }
  };

  const handleGoToDatos = () => {
    setOpenSections((prev) => (prev.includes('datos') ? prev : ['datos', ...prev]));
    setTimeout(() => {
      if (datosRef.current) datosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 300);
  };

  // Handler del FAB - Abre el acordeón correspondiente y hace scroll
  const handleFABAction = (actionType) => {
    if (actionType === 'datos') { handleGoToDatos(); return; }
    // Map de acciones a secciones del acordeón
    const sectionMap = {
      'actuacion': { ref: actuacionesRef, accordion: 'actuaciones' },
      'tarea': { ref: tareasRef, accordion: 'tareas' },
      'nota': { ref: notasRef, accordion: 'notas' },
      'evento': { ref: eventosRef, accordion: 'eventos' }
    };
    
    const section = sectionMap[actionType];
    if (!section) return;
    setOpenSections((prev) => (prev.includes(section.accordion) ? prev : [...prev, section.accordion]));
    setTimeout(() => {
      if (section.ref?.current) {
        section.ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const btn = section.ref.current.querySelector('[data-fab-trigger="true"]');
        if (btn) btn.click();
      }
    }, 350);
    return;
    if (section?.ref?.current) {
      // Scroll suave a la sección
      section.ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Simular click en el botón de nueva (después del scroll)
      setTimeout(() => {
        const newButton = section.ref.current.querySelector('[data-fab-trigger="true"]');
        if (newButton) {
          newButton.click();
        }
      }, 500);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!expediente) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Expediente no encontrado</p>
        <Button onClick={() => navigate(getBackDestination())} className="mt-4">Volver a expedientes</Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6 relative">
      {/* Botones de navegación flotantes */}
      {navegacion.anterior && (
        <button
          onClick={() => navigate(`/expedientes/${navegacion.anterior.id}`)}
          className="fixed left-2 top-1/2 -translate-y-1/2 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all hover:scale-110 group"
          title={`Anterior: ${navegacion.anterior.numero_expediente}`}
        >
          <ChevronLeft className="w-6 h-6" />
          <span className="absolute left-full ml-2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            {navegacion.anterior.numero_expediente}
          </span>
        </button>
      )}
      {navegacion.siguiente && (
        <button
          onClick={() => navigate(`/expedientes/${navegacion.siguiente.id}`)}
          className="fixed right-2 top-1/2 -translate-y-1/2 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all hover:scale-110 group"
          title={`Siguiente: ${navegacion.siguiente.numero_expediente}`}
        >
          <ChevronRight className="w-6 h-6" />
          <span className="absolute right-full mr-2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            {navegacion.siguiente.numero_expediente}
          </span>
        </button>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(getBackDestination())}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📁 {expediente.numero_expediente}</h1>
            <p className="text-lg text-gray-800 mt-1 font-medium">{expediente.titulo}</p>
            {expediente.descripcion && expediente.descripcion !== expediente.titulo && (
              <p className="text-gray-600 mt-1 text-sm">{expediente.descripcion}</p>
            )}
            {/* Mostrar cliente si existe */}
            {(() => {
              const cliente = participantes.find(p => p.rol === 'cliente');
              if (cliente) {
                return (
                  <p className="text-blue-600 mt-1 text-sm font-medium flex items-center gap-1">
                    <User className="w-3 h-3" /> Cliente: {cliente.nombre}
                  </p>
                );
              }
              return null;
            })()}
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="flex flex-wrap gap-2">
          {/* ACTUACIÓN IA */}
          <Button 
            variant="outline" 
            className="bg-amber-600 text-white hover:bg-amber-700"
            onClick={() => setIsIADialogOpen(true)}
            data-testid="btn-actuacion-ia-header"
          >
            <Sparkles className="w-4 h-4 mr-2" />ACTUACIÓN IA
          </Button>

          {/* + NUEVA ACTUACIÓN */}
          <Button 
            variant="outline" 
            className="bg-blue-700 text-white hover:bg-blue-800"
            onClick={() => setIsActuacionDialogOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />NUEVA ACTUACIÓN
          </Button>

          {/* ENCARGAR A IA */}
          <EncargarIADialog expediente={expediente} />

          {/* EDITAR - Incluye gestión de participantes */}
          <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (open) { setEditTab('datos'); loadContactosDisponibles(); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-green-600 text-white hover:bg-green-700">
                <Edit className="w-4 h-4 mr-2" />EDITAR
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5" />Editar expediente {expediente.serie}-{expediente.codigo}</DialogTitle>
              </DialogHeader>
              
              <Tabs value={editTab} onValueChange={setEditTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="datos" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />Datos
                  </TabsTrigger>
                  <TabsTrigger value="participantes" className="flex items-center gap-2">
                    <Users className="w-4 h-4" />Participantes
                  </TabsTrigger>
                </TabsList>

                {/* TAB: DATOS DEL EXPEDIENTE */}
                <TabsContent value="datos" className="mt-4">
                  <form onSubmit={handleEditExpediente}>
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Datos básicos</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div><Label>Nº Expediente {!isAdmin && <span className="text-xs text-gray-400">(solo admin)</span>}</Label><Input value={editForm.numero_expediente} onChange={(e) => setEditForm({...editForm, numero_expediente: e.target.value})} placeholder="Ej: EXP13303" disabled={!isAdmin} className={!isAdmin ? "bg-gray-100" : ""} /></div>
                          <div><Label>Expediente Judicial (histórico)</Label><Input value={editForm.expediente_judicial} onChange={(e) => setEditForm({...editForm, expediente_judicial: e.target.value})} placeholder="Ej: 123/2025 (usa la lista de Procedimientos judiciales)" /></div>
                          <div><Label>NIG</Label><Input value={editForm.nig} onChange={(e) => setEditForm({...editForm, nig: e.target.value})} placeholder="Número Identificación General" /></div>
                          <div><Label>Nº Reclamación Extrajudicial</Label><Input value={editForm.referencia_extrajudicial} onChange={(e) => setEditForm({...editForm, referencia_extrajudicial: e.target.value})} placeholder="Ej: EB-2026-00001" /></div>
                        </div>
                        <div className="mt-4">
                          <Label>Título *</Label>
                          <Input value={editForm.titulo} onChange={(e) => setEditForm({...editForm, titulo: e.target.value})} required placeholder="Título del expediente" />
                        </div>
                        <div className="mt-4">
                          <Label>Descripción</Label>
                          <Textarea value={editForm.descripcion} onChange={(e) => setEditForm({...editForm, descripcion: e.target.value})} rows={3} placeholder="Descripción detallada del expediente (opcional)" />
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Tag className="w-4 h-4" /> Clasificación</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Rama</Label>
                            <Select value={editForm.rama || editForm.tipo} onValueChange={(v) => setEditForm({...editForm, rama: v, tipo: v})}>
                              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Civil">Civil</SelectItem>
                                <SelectItem value="Mercantil">Mercantil</SelectItem>
                                <SelectItem value="Laboral">Laboral</SelectItem>
                                <SelectItem value="Penal">Penal</SelectItem>
                                <SelectItem value="Administrativo">Administrativo</SelectItem>
                                <SelectItem value="Familia">Familia</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Tipo Procedimiento</Label>
                            <Select value={editForm.tipo_procedimiento || editForm.clase} onValueChange={(v) => setEditForm({...editForm, tipo_procedimiento: v, clase: v})}>
                              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                              <SelectContent>
                                {tiposProcedimiento.map(tp => (
                                  <SelectItem key={tp} value={tp}>{tp}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Estado</Label>
                            <Select value={editForm.estado} onValueChange={(v) => setEditForm({...editForm, estado: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="activo">Activo</SelectItem>
                                <SelectItem value="suspendido">Suspendido</SelectItem>
                                <SelectItem value="cerrado">Cerrado</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> Datos procesales</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Fase procesal</Label>
                            <Select value={editForm.fase} onValueChange={(v) => setEditForm({...editForm, fase: v})}>
                              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                              <SelectContent>
                                {fasesConfig.map(fase => (
                                  <SelectItem key={fase} value={fase}>{fase}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div><Label>Fecha inicio</Label><Input type="date" value={editForm.fecha_inicio} onChange={(e) => setEditForm({...editForm, fecha_inicio: e.target.value})} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div><Label>Contrario</Label><Input value={editForm.contrario} onChange={(e) => setEditForm({...editForm, contrario: e.target.value})} placeholder="Parte contraria" /></div>
                          <div><Label>Valor económico (€)</Label><Input type="number" value={editForm.valor_economico} onChange={(e) => setEditForm({...editForm, valor_economico: parseFloat(e.target.value) || 0})} /></div>
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Observaciones</h3>
                        <Textarea value={editForm.observaciones} onChange={(e) => setEditForm({...editForm, observaciones: e.target.value})} rows={4} />
                      </div>
                    </div>
                    <DialogFooter className="mt-6">
                      <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar cambios
                      </Button>
                    </DialogFooter>
                  </form>
                </TabsContent>

                {/* TAB: PARTICIPANTES */}
                <TabsContent value="participantes" className="mt-4">
                  <div className="space-y-6">
                    {/* Añadir participante */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <UserPlus className="w-4 h-4" /> Añadir participante de la base de datos
                      </h3>
                      
                      {/* Tipo de participante */}
                      <div className="flex gap-2 mb-4">
                        <Button 
                          type="button"
                          data-testid="btn-tipo-contacto"
                          variant={tipoParticipante === 'contacto' ? 'default' : 'outline'}
                          onClick={() => {
                            setTipoParticipante('contacto');
                            setSelectedContacto(null);
                            if (selectedRol === 'organismo_asignado') setSelectedRol('cliente');
                          }}
                          className="flex-1"
                        >
                          <User className="w-4 h-4 mr-2" />Contactos
                        </Button>
                        <Button 
                          type="button"
                          data-testid="btn-tipo-empresa"
                          variant={tipoParticipante === 'empresa' ? 'default' : 'outline'}
                          onClick={() => {
                            setTipoParticipante('empresa');
                            setSelectedContacto(null);
                            if (selectedRol === 'organismo_asignado') setSelectedRol('cliente');
                          }}
                          className="flex-1"
                        >
                          <Building2 className="w-4 h-4 mr-2" />Empresas
                        </Button>
                        <Button 
                          type="button"
                          data-testid="btn-tipo-organismo"
                          variant={tipoParticipante === 'organismo' ? 'default' : 'outline'}
                          onClick={() => {
                            setTipoParticipante('organismo');
                            setSelectedContacto(null);
                            setSelectedRol('organismo_asignado');
                          }}
                          className="flex-1"
                        >
                          <Building2 className="w-4 h-4 mr-2" />Organismos
                        </Button>
                      </div>

                      {/* Selector de rol — filtrado según tipoParticipante */}
                      <div className="mb-4">
                        <Label>Rol del participante *</Label>
                        <Select value={selectedRol} onValueChange={setSelectedRol}>
                          <SelectTrigger className="mt-1" data-testid="select-rol-participante"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLES_PARTICIPANTE
                              .filter(rol => tipoParticipante === 'organismo'
                                ? ROLES_ORGANISMO.includes(rol.value)
                                : rol.value !== 'organismo_asignado')
                              .map(rol => (
                                <SelectItem key={rol.value} value={rol.value}>{rol.label}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {tipoParticipante === 'organismo' && (
                          <p className="text-xs text-gray-500 mt-1">
                            Por defecto "Organismo asignado" (aparece como juzgado del expediente). Selecciona "Cliente" si el organismo es el cliente del caso, o "Parte contraria" si es la parte demandada (p.ej. un organismo público).
                          </p>
                        )}
                      </div>

                      {/* Buscador */}
                      <div className="mb-4">
                        <Label>Buscar {tipoParticipante === 'contacto' ? 'contacto' : tipoParticipante === 'empresa' ? 'empresa' : 'organismo'}</Label>
                        <div className="relative mt-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input 
                            value={searchContacto}
                            onChange={(e) => setSearchContacto(e.target.value)}
                            placeholder="Buscar por nombre, email o NIF..."
                            className="pl-10"
                          />
                        </div>
                      </div>

                      {/* Lista de resultados */}
                      <div className="border rounded-lg max-h-[200px] overflow-y-auto bg-white mb-4">
                        {loadingContactos ? (
                          <div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
                        ) : (tipoParticipante === 'contacto' ? filteredContactos : tipoParticipante === 'empresa' ? filteredEmpresas : filteredOrganismos).length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            <p>No se encontraron {tipoParticipante === 'contacto' ? 'contactos' : tipoParticipante === 'empresa' ? 'empresas' : 'organismos'}</p>
                            {tipoParticipante === 'contacto' && !showNewContactForm && (
                              <Button 
                                type="button"
                                variant="link" 
                                className="mt-2 text-blue-600"
                                onClick={() => setShowNewContactForm(true)}
                              >
                                <Plus className="w-4 h-4 mr-1" /> Crear nuevo contacto
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="divide-y">
                            {(tipoParticipante === 'contacto' ? filteredContactos : tipoParticipante === 'empresa' ? filteredEmpresas : filteredOrganismos).map((item) => (
                              <div 
                                key={item.id}
                                onClick={() => setSelectedContacto(item)}
                                className={`p-3 cursor-pointer hover:bg-blue-50 transition-colors ${selectedContacto?.id === item.id ? 'bg-blue-100 border-l-4 border-blue-600' : ''}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tipoParticipante === 'contacto' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                                    {tipoParticipante === 'contacto' ? <User className="w-4 h-4 text-blue-600" /> : <Building2 className="w-4 h-4 text-purple-600" />}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">
                                      {item.nombre_completo || [item.nombre, item.primer_apellido, item.segundo_apellido].filter(Boolean).join(' ') || item.nombre}
                                    </p>
                                    <div className="flex gap-3 text-xs text-gray-500">
                                      {item.email && <span>✉️ {item.email}</span>}
                                      {item.telefono && <span>📞 {item.telefono}</span>}
                                    </div>
                                  </div>
                                  {selectedContacto?.id === item.id && <CheckCircle className="w-5 h-5 text-blue-600" />}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Contacto seleccionado */}
                      {selectedContacto && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                          <p className="text-sm text-blue-700">
                            <strong>Seleccionado:</strong> {selectedContacto.nombre_completo || [selectedContacto.nombre, selectedContacto.primer_apellido, selectedContacto.segundo_apellido].filter(Boolean).join(' ') || selectedContacto.nombre} como <strong>{ROLES_PARTICIPANTE.find(r => r.value === selectedRol)?.label}</strong>
                          </p>
                        </div>
                      )}

                      {/* Formulario para crear nuevo contacto */}
                      {showNewContactForm && tipoParticipante === 'contacto' && (
                        <div className="border-2 border-dashed border-green-300 rounded-lg p-4 mb-4 bg-green-50">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-green-800 flex items-center gap-2">
                              <UserPlus className="w-4 h-4" /> Crear nuevo contacto
                            </h4>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setShowNewContactForm(false);
                                setNewContactoForm({ nombre: '', nif_cif: '', email: '', telefono: '', direccion: '', tipo: 'cliente' });
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                              <Label className="text-xs">Nombre *</Label>
                              <Input 
                                value={newContactoForm.nombre}
                                onChange={(e) => setNewContactoForm({...newContactoForm, nombre: e.target.value})}
                                placeholder="Nombre completo"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">NIF/CIF</Label>
                              <Input 
                                value={newContactoForm.nif_cif}
                                onChange={(e) => setNewContactoForm({...newContactoForm, nif_cif: e.target.value})}
                                placeholder="12345678A"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Teléfono</Label>
                              <Input 
                                value={newContactoForm.telefono}
                                onChange={(e) => setNewContactoForm({...newContactoForm, telefono: e.target.value})}
                                placeholder="600 000 000"
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Email</Label>
                              <Input 
                                type="email"
                                value={newContactoForm.email}
                                onChange={(e) => setNewContactoForm({...newContactoForm, email: e.target.value})}
                                placeholder="email@ejemplo.com"
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Dirección</Label>
                              <Input 
                                value={newContactoForm.direccion}
                                onChange={(e) => setNewContactoForm({...newContactoForm, direccion: e.target.value})}
                                placeholder="Calle, número, ciudad..."
                                className="mt-1"
                              />
                            </div>
                          </div>

                          <div className="mt-4 p-2 bg-green-100 rounded text-xs text-green-800">
                            <strong>Nota:</strong> El contacto se creará y se añadirá automáticamente como <strong>{ROLES_PARTICIPANTE.find(r => r.value === selectedRol)?.label || 'participante'}</strong>
                          </div>

                          <Button 
                            type="button"
                            onClick={handleCreateContactoAndAddParticipante}
                            disabled={saving || !newContactoForm.nombre.trim() || !selectedRol}
                            className="w-full mt-3 bg-green-600 hover:bg-green-700"
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                            Crear contacto y añadir como participante
                          </Button>
                        </div>
                      )}

                      {/* Botón para mostrar formulario cuando hay resultados */}
                      {!showNewContactForm && tipoParticipante === 'contacto' && filteredContactos.length > 0 && (
                        <Button 
                          type="button"
                          variant="outline" 
                          size="sm"
                          className="w-full mb-4 border-dashed"
                          onClick={() => setShowNewContactForm(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" /> ¿No encuentras el contacto? Crear nuevo
                        </Button>
                      )}

                      <Button onClick={handleAddParticipante} disabled={!selectedContacto || saving} className="w-full bg-blue-600 hover:bg-blue-700">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        Añadir participante
                      </Button>
                    </div>

                    {/* Lista de participantes actuales */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Participantes actuales ({participantes.length})
                      </h3>
                      
                      {participantes.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                          <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                          <p className="text-gray-500">No hay participantes registrados</p>
                          <p className="text-sm text-gray-400 mt-1">Usa el buscador de arriba para añadir participantes</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[300px] overflow-y-auto">
                          {Object.entries(participantesPorRol).map(([rol, lista]) => (
                            <div key={rol} className="border rounded-lg p-3">
                              <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                                <User className="w-4 h-4 text-blue-600" />
                                {rol}
                                <Badge variant="outline" className="ml-2">{lista.length}</Badge>
                              </h4>
                              <div className="space-y-2">
                                {lista.map((p) => (
                                  <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${p.tipo_participante === 'organismo' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                                        {p.tipo_participante === 'organismo' ? <Building2 className="w-4 h-4 text-purple-600" /> : <User className="w-4 h-4 text-blue-600" />}
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-900 text-sm">{p.nombre_completo || [p.nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean).join(' ') || p.nombre || 'Sin nombre'}</p>
                                        <div className="flex gap-2 text-xs text-gray-500">
                                          {p.email && <span>✉️ {p.email}</span>}
                                          {p.telefono && <span>📞 {p.telefono}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => handleRemoveParticipante(p.id)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <DialogFooter className="mt-6">
                    <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cerrar</Button>
                  </DialogFooter>
                </TabsContent>


              </Tabs>
            </DialogContent>
          </Dialog>

          {/* CREAR CONTACTO - Formulario completo del módulo de contactos */}
          <Button variant="outline" className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => setIsContactoWizardOpen(true)}>
            <Users className="w-4 h-4 mr-2" />CREAR CONTACTO
          </Button>
          <ContactoWizard
            isOpen={isContactoWizardOpen}
            onClose={() => setIsContactoWizardOpen(false)}
            onSaved={() => { setIsContactoWizardOpen(false); loadExpediente(); }}
          />

          {/* DESCARGAR / IMPRIMIR: 2 opciones */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={generandoIter} data-testid="btn-descargar-imprimir">
                {generandoIter
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Download className="w-4 h-4 mr-2" />}
                DESCARGAR/IMPRIMIR
                <ChevronDown className="w-3 h-3 ml-2 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel>Generar documento</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDownloadReport} data-testid="btn-word-datos">
                <FileText className="w-4 h-4 mr-2 text-blue-600" />
                <div className="flex flex-col">
                  <span className="font-medium">Datos y actuaciones</span>
                  <span className="text-xs text-gray-500">Informe Word (.docx) con todos los datos</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleGenerarIterProcesal}
                disabled={generandoIter}
                data-testid="btn-iter-procesal"
              >
                <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                <div className="flex flex-col">
                  <span className="font-medium">Análisis del iter procesal</span>
                  <span className="text-xs text-gray-500">
                    PDF con IA (Gemini 3 Pro) — 1-3 min
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* DESCARGAR ARCHIVOS ADJUNTOS (ZIP) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={downloadingZip} data-testid="btn-descargar-adjuntos">
                {downloadingZip
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <FileArchive className="w-4 h-4 mr-2" />}
                DESCARGAR ARCHIVOS
                <ChevronDown className="w-3 h-3 ml-2 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Adjuntos del expediente</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDownloadAdjuntosZip('original')}
                data-testid="btn-zip-original"
              >
                <FileArchive className="w-4 h-4 mr-2 text-blue-600" />
                <div className="flex flex-col">
                  <span className="font-medium">Formato original</span>
                  <span className="text-xs text-gray-500">PDF, Word, etc. tal cual</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDownloadAdjuntosZip('markdown')}
                data-testid="btn-zip-markdown"
              >
                <Brain className="w-4 h-4 mr-2 text-purple-600" />
                <div className="flex flex-col">
                  <span className="font-medium">Convertir a Markdown</span>
                  <span className="text-xs text-gray-500">Para uso en LLMs / IA</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* GENERAR ESCRITO WORD */}
          <Button
            variant="outline"
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={openGenerarEscrito}
            data-testid="btn-generar-escrito"
          >
            <FileText className="w-4 h-4 mr-2" />GENERAR ESCRITO
          </Button>

          {/* AUDITAR CON IA (hotfix-pos) */}
          <Button
            variant="outline"
            className="bg-slate-800 text-white hover:bg-slate-900 hover:text-white"
            onClick={handleAuditarIA}
            disabled={auditandoIA}
            data-testid="auditar-ia-btn"
          >
            {auditandoIA ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
            AUDITAR CON IA
          </Button>

          {/* Dialog IA Actuación - header */}
          <NuevaActuacionIA
            expedienteId={expediente?.id}
            open={isIADialogOpen}
            onOpenChange={setIsIADialogOpen}
            onSuccess={() => loadExpediente()}
          />
        </div>
      </div>

      {/* Dialog: Generar Escrito Word */}
      <Dialog open={isGenerarEscritoOpen} onOpenChange={setIsGenerarEscritoOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Generar escrito judicial
            </DialogTitle>
            <DialogDescription>
              Selecciona una plantilla Word para generar el documento con los datos de este expediente
            </DialogDescription>
          </DialogHeader>

          {loadingPlantillas ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : plantillasWord.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 mb-2">No hay plantillas Word configuradas</p>
              <p className="text-sm text-gray-400">
                Ve a Configuracion &gt; Expedientes &gt; Plantillas Word Judiciales para crear plantillas
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Buscador de plantillas */}
              <div className="relative" data-testid="buscador-plantillas">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar plantilla por nombre o descripcion..."
                  value={busquedaPlantilla}
                  onChange={(e) => setBusquedaPlantilla(e.target.value)}
                  className="pl-9"
                  data-testid="input-buscar-plantilla"
                />
              </div>

              {/* Selección de plantilla - con carpetas */}
              <div>
                <label className="text-sm font-medium mb-2 block">Selecciona plantilla:</label>
                <div className="max-h-72 overflow-y-auto border rounded-lg">
                  {(() => {
                    const termino = busquedaPlantilla.toLowerCase().trim();
                    const filtrar = (p) => {
                      if (!termino) return true;
                      return (p.nombre || '').toLowerCase().includes(termino) ||
                             (p.descripcion || '').toLowerCase().includes(termino);
                    };
                    const plantillasFiltradas = plantillasWord.filter(filtrar);
                    const sinCarpetaFiltradas = plantillasFiltradas.filter(p => !p.carpeta_id);
                    const hayResultados = plantillasFiltradas.length > 0;

                    if (!hayResultados) {
                      return (
                        <p className="text-center text-sm text-gray-400 py-4" data-testid="no-resultados-plantilla">
                          No se encontraron plantillas con "{busquedaPlantilla}"
                        </p>
                      );
                    }

                    return (
                      <div className="divide-y">
                        {/* Carpetas con sus plantillas */}
                        {carpetasWord.map(carpeta => {
                          const plantillasEnCarpeta = plantillasFiltradas.filter(p => p.carpeta_id === carpeta.id);
                          if (plantillasEnCarpeta.length === 0) return null;
                          const isExpanded = expandedFoldersEscrito[carpeta.id];
                          return (
                            <div key={`c-${carpeta.id}`}>
                              <div
                                className="flex items-center gap-2 px-3 py-2 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
                                onClick={() => setExpandedFoldersEscrito(prev => ({...prev, [carpeta.id]: !prev[carpeta.id]}))}
                                data-testid={`escrito-carpeta-${carpeta.id}`}
                              >
                                {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-amber-600" /> : <ChevronRight className="w-3.5 h-3.5 text-amber-600" />}
                                {isExpanded ? <FolderOpen className="w-4 h-4 text-amber-500" /> : <Folder className="w-4 h-4 text-amber-500" />}
                                <span className="text-sm font-medium text-gray-800">{carpeta.nombre}</span>
                                <span className="text-xs text-gray-400 ml-1">({plantillasEnCarpeta.length})</span>
                              </div>
                              {isExpanded && (
                                <div className="pl-4">
                                  {plantillasEnCarpeta.map(p => (
                                    <div
                                      key={p.id}
                                      onClick={() => setSelectedPlantilla(p)}
                                      className={`p-2.5 cursor-pointer transition-all border-l-2 ${
                                        selectedPlantilla?.id === p.id
                                          ? 'border-l-purple-500 bg-purple-50'
                                          : 'border-l-transparent hover:border-l-purple-300 hover:bg-gray-50'
                                      }`}
                                      data-testid={`plantilla-option-${p.id}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <FileText className={`w-4 h-4 flex-shrink-0 ${selectedPlantilla?.id === p.id ? 'text-purple-600' : 'text-gray-400'}`} />
                                        <div className="min-w-0">
                                          <p className="font-medium text-sm truncate">{p.nombre}</p>
                                          {p.descripcion && <p className="text-xs text-gray-500 truncate">{p.descripcion}</p>}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {/* Plantillas sin carpeta */}
                        {sinCarpetaFiltradas.length > 0 && (
                          <div>
                            {carpetasWord.length > 0 && (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50">
                                <FileText className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-500">Sin carpeta</span>
                                <span className="text-xs text-gray-400">({sinCarpetaFiltradas.length})</span>
                              </div>
                            )}
                            {sinCarpetaFiltradas.map(p => (
                              <div
                                key={p.id}
                                onClick={() => setSelectedPlantilla(p)}
                                className={`p-2.5 cursor-pointer transition-all ${
                                  carpetasWord.length > 0 ? 'pl-4 border-l-2' : 'border-l-2'
                                } ${
                                  selectedPlantilla?.id === p.id
                                    ? 'border-l-purple-500 bg-purple-50'
                                    : 'border-l-transparent hover:border-l-purple-300 hover:bg-gray-50'
                                }`}
                                data-testid={`plantilla-option-${p.id}`}
                              >
                                <div className="flex items-center gap-2">
                                  <FileText className={`w-4 h-4 flex-shrink-0 ${selectedPlantilla?.id === p.id ? 'text-purple-600' : 'text-gray-400'}`} />
                                  <div className="min-w-0">
                                    <p className="font-medium text-sm truncate">{p.nombre}</p>
                                    {p.descripcion && <p className="text-xs text-gray-500 truncate">{p.descripcion}</p>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Selector de procedimiento judicial (para {{EXPEDIENTE_JUDICIAL}}) */}
              {procedimientosEscrito.length > 0 && (
                <div data-testid="selector-procedimiento-escrito">
                  <label className="text-sm font-medium mb-2 block">
                    Procedimiento judicial a usar en el escrito:
                  </label>
                  <Select
                    value={selectedProcedimientoId != null ? String(selectedProcedimientoId) : ''}
                    onValueChange={cambiarProcedimientoEscrito}
                  >
                    <SelectTrigger data-testid="select-procedimiento-escrito">
                      <SelectValue placeholder="Selecciona un procedimiento" />
                    </SelectTrigger>
                    <SelectContent>
                      {procedimientosEscrito.map(p => (
                        <SelectItem key={p.id} value={String(p.id)} data-testid={`opcion-procedimiento-${p.id}`}>
                          {`${p.tipo || ''} ${p.numero || ''}`.trim()}{p.organo ? ` — ${p.organo}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {procedimientosEscrito.length > 1
                      ? 'Este expediente tiene varios procedimientos. Elige cuál se insertará en {{EXPEDIENTE_JUDICIAL}}.'
                      : 'El campo {{EXPEDIENTE_JUDICIAL}} se rellenará con este procedimiento. El histórico está en {{EXPEDIENTE_JUDICIAL_HISTORICO}}.'}
                  </p>
                </div>
              )}

              {/* Preview de datos */}
              {previewDatos && (
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Datos del expediente ({previewDatos.total_con_valor} de {previewDatos.datos.length} campos con valor):
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-64 overflow-y-auto space-y-1">
                    {previewDatos.datos.map((d, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <code className={`px-1 rounded font-mono min-w-[180px] flex-shrink-0 ${
                          d.tiene_valor ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {d.variable}
                        </code>
                        <span className={d.tiene_valor ? 'text-gray-900' : 'text-gray-400 italic'}>
                          {d.valor || 'Sin datos'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsGenerarEscritoOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleGenerarEscrito}
              disabled={!selectedPlantilla || generando}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="btn-confirmar-generar"
            >
              {generando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Generar y descargar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <FirmasExpedientePanel expedienteId={parseInt(id)} />

      {/* Acordeón de secciones */}
      <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="space-y-4">
        
        {/* DATOS DEL EXPEDIENTE */}
        
        {/* EXPEDIENTES RELACIONADOS (v50) */}
        <AccordionItem value="relacionados" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white">🔗</div>
              Expedientes relacionados
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <ExpedientesRelacionadosCard expedienteId={expediente.id} canEdit={true} />
          </AccordionContent>
        </AccordionItem>

<AccordionItem value="datos" className="bg-white border rounded-lg" ref={datosRef}>
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">📋</div>
              Datos del expediente
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Información básica</h3>
                <div className="space-y-3 text-sm">
                  <div><span className="text-gray-500">Nº Expediente:</span><span className="ml-2 font-medium text-blue-700">{expediente.numero_expediente}</span></div>
                  <div><span className="text-gray-500">Expediente Judicial (histórico):</span><span className="ml-2 font-medium">{expediente.expediente_judicial || 'No especificado'}</span></div>
                  <div><span className="text-gray-500">NIG:</span><span className="ml-2 font-medium">{expediente.nig || 'No especificado'}</span></div>
                  {expediente.referencia_extrajudicial && (
                    <div><span className="text-gray-500">Nº Reclamación Extrajudicial:</span><span className="ml-2 font-medium text-orange-600">{expediente.referencia_extrajudicial}</span></div>
                  )}
                  <div><span className="text-gray-500">Estado:</span><Badge className="ml-2 bg-green-100 text-green-800">{expediente.estado}</Badge></div>
                  <div><span className="text-gray-500">Descripción:</span><p className="mt-1 text-gray-900">{expediente.descripcion}</p></div>
                  <div><span className="text-gray-500">Tipo:</span><span className="ml-2 font-medium">{expediente.tipo_procedimiento || expediente.tipo || 'No especificado'}</span></div>
                  <div><span className="text-gray-500">Rama:</span><span className="ml-2 font-medium">{expediente.rama || 'No especificada'}</span></div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Datos jurídicos y participantes clave</h3>
                <div className="space-y-3 text-sm">
                  <div><span className="text-gray-500">Fase:</span><span className="ml-2 font-medium">{expediente.fase || 'No especificada'}</span></div>
                  <div>
                    <span className="text-gray-500">Juzgado:</span>
                    {expediente.juzgado_id && expediente.juzgado_nombre ? (
                      <a href={`/contactos/${expediente.juzgado_id}`} target="_blank" rel="noopener noreferrer" className="ml-2 font-medium text-blue-600 hover:underline">
                        {expediente.juzgado_nombre}
                      </a>
                    ) : (
                      <span className="ml-2 text-gray-900">{expediente.juzgado || 'No especificado'}</span>
                    )}
                  </div>
                  <div><span className="text-gray-500">Fecha inicio:</span><span className="ml-2 font-medium">{expediente.fecha_inicio ? new Date(expediente.fecha_inicio).toLocaleDateString('es-ES') : 'No especificada'}</span></div>
                  <div><span className="text-gray-500">Valor económico:</span><span className="ml-2 font-medium">{expediente.valor_economico ? `${expediente.valor_economico.toLocaleString('es-ES')} €` : 'No especificado'}</span></div>
                  
                  {participantes.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Participantes asociados
                      </h4>
                      <div className="space-y-3">
                        {(() => {
                          const porRol = participantes.reduce((acc, p) => {
                            const rolLabel = p.rol_display || p.rol;
                            if (!acc[rolLabel]) acc[rolLabel] = [];
                            acc[rolLabel].push(p);
                            return acc;
                          }, {});
                          const ordenRoles = ['Cliente', 'Potencial cliente', 'Abogado propio', 'Abogado colaborador', 'Procurador propio', 'Colaborador', 'Asesor', 'Perito', 'Testigo propio', 'Parte contraria', 'Abogado contrario', 'Procurador contrario', 'Testigo de la parte contraria', 'Gestor', 'Intermediario', 'Otorgante', 'Proveedor', 'Otro'];
                          const rolesOrdenados = Object.keys(porRol).sort((a, b) => {
                            const indexA = ordenRoles.indexOf(a);
                            const indexB = ordenRoles.indexOf(b);
                            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                            if (indexA === -1) return 1;
                            if (indexB === -1) return -1;
                            return indexA - indexB;
                          });
                          return rolesOrdenados.map(rolLabel => (
                            <div key={rolLabel} className="flex items-start">
                              <span className="text-gray-500 min-w-[140px] flex-shrink-0">{rolLabel}:</span>
                              <div className="ml-2 flex-1 space-y-1">
                                {porRol[rolLabel].map((p) => {
                                  const contactId = p.contacto_id || p.organismo_id;
                                  const colorClass = p.rol === 'cliente' || p.rol === 'potencial_cliente' ? 'text-blue-600' 
                                    : p.rol === 'parte_contraria' || p.rol === 'abogado_contrario' || p.rol === 'procurador_contrario' ? 'text-red-600'
                                    : p.rol === 'procurador_propio' || p.rol === 'abogado_propio' ? 'text-green-600'
                                    : p.rol === 'colaborador' || p.rol === 'abogado_colaborador' ? 'text-purple-600'
                                    : 'text-gray-700';
                                  return (
                                    <div key={p.id} className="flex flex-wrap items-center gap-x-2">
                                      {contactId ? (
                                        <a href={`/contactos/${contactId}`} target="_blank" rel="noopener noreferrer" className={`font-medium ${colorClass} hover:underline`}>
                                          {p.nombre || 'Sin nombre'}
                                        </a>
                                      ) : (
                                        <span className={`font-medium ${colorClass}`}>{p.nombre || 'Sin nombre'}</span>
                                      )}
                                      {p.telefono && <span className="text-gray-500 text-xs">📞 {p.telefono}</span>}
                                      {p.email && <span className="text-gray-500 text-xs">✉️ {p.email}</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                  
                  {participantes.length === 0 && expediente.contrario && (
                    <div className="flex items-start">
                      <span className="text-gray-500 min-w-[100px]">Contrario:</span>
                      <span className="ml-2 font-medium text-red-600">{expediente.contrario}</span>
                    </div>
                  )}
                  {participantes.length === 0 && expediente.procurador && (
                    <div className="flex items-start">
                      <span className="text-gray-500 min-w-[100px]">Procurador:</span>
                      <span className="ml-2 font-medium text-green-600">{expediente.procurador}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <ProcedimientosJudiciales expedienteId={expediente.id} historicoTexto={expediente.expediente_judicial} canEdit={true} onOrganoAsignado={loadParticipantes} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ACTUACIONES */}
        <AccordionItem value="actuaciones" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white">📝</div>
              Actuaciones ({actuaciones.length})
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div ref={actuacionesRef}>
              <ActuacionesSection
                expedienteId={parseInt(id)}
                actuaciones={actuaciones}
                onCreateActuacion={handleCreateActuacionNew}
                onRefresh={loadExpediente}
                isDialogOpenExternal={isActuacionDialogOpen}
                onDialogOpenChange={setIsActuacionDialogOpen}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ANÁLISIS IA */}
        <AccordionItem value="analisis-ia" className="bg-white border rounded-lg border-l-4 border-l-purple-500">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center text-white">✨</div>
              Análisis IA
              <Badge className="ml-2 bg-purple-100 text-purple-700">Nuevo</Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <AnalisisIAExpediente expediente={expediente} actuaciones={actuaciones} />
          </AccordionContent>
        </AccordionItem>

        {/* PAGOS */}
        <AccordionItem value="pagos" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white">💰</div>
              Pagos
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <PagosSection expedienteId={parseInt(id)} />
          </AccordionContent>
        </AccordionItem>

        {/* PAGO DE TASAS */}
        <AccordionItem value="pago-tasas" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white">🧾</div>
              Pago de Tasas
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <PagoTasasSection expedienteId={parseInt(id)} />
          </AccordionContent>
        </AccordionItem>

        {/* CLÁUSULAS */}
        <AccordionItem value="clausulas" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white">⚖️</div>
              Cláusulas
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <ClausulasSection expedienteId={parseInt(id)} />
          </AccordionContent>
        </AccordionItem>

        {/* PARTICIPANTES */}
        <AccordionItem value="participantes" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">👥</div>
              Participantes ({participantes.length})
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">Personas y organismos relacionados con este expediente</p>
              <Button size="sm" onClick={() => { setIsEditDialogOpen(true); setEditTab('participantes'); }} className="bg-blue-700 hover:bg-blue-800">
                <Edit className="w-4 h-4 mr-2" />GESTIONAR PARTICIPANTES
              </Button>
            </div>

            {participantes.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No hay participantes registrados</p>
                <p className="text-sm text-gray-400 mt-1">Usa el botón "Editar" para añadir participantes</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(participantesPorRol).map(([rol, lista]) => (
                  <div key={rol} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" />{rol}<Badge variant="outline" className="ml-2">{lista.length}</Badge>
                    </h4>
                    <div className="space-y-2">
                      {lista.map((p) => (
                        <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${p.tipo_participante === 'organismo' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                              {p.tipo_participante === 'organismo' ? <Building2 className="w-4 h-4 text-purple-600" /> : <User className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div>
                              <a href={`/contactos/${p.contacto_id}`} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">{p.nombre_completo || [p.nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean).join(' ') || p.nombre || 'Sin nombre'}</a>
                              <div className="flex gap-3 text-xs text-gray-500">
                                {p.email && <span>✉️ {p.email}</span>}
                                {p.telefono && <span>📞 {p.telefono}</span>}
                              </div>
                            </div>
                          </div>
                          {/* Botón dar acceso al portal para clientes */}
                          {p.rol === 'cliente' && p.contacto_id && p.email && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal-cliente/registrar-con-envio`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      contacto_id: p.contacto_id,
                                      email: p.email,
                                      expedientes_ids: [parseInt(id)]
                                    })
                                  });
                                  const data = await response.json();
                                  if (response.ok && data.success) {
                                    toast({
                                      title: 'Acceso al portal creado',
                                      description: data.email_enviado 
                                        ? `Código enviado a ${p.email}` 
                                        : `Código: ${data.codigo_acceso}`
                                    });
                                  } else {
                                    throw new Error(data.detail || 'Error');
                                  }
                                } catch (err) {
                                  toast({ title: 'Error', description: err.message, variant: 'destructive' });
                                }
                              }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Dar acceso al portal del cliente"
                            >
                              <Shield className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* TAREAS */}
        <AccordionItem value="tareas" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">✓</div>
              Tareas
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div ref={tareasRef}>
              <TareasSection expedienteId={parseInt(id)} tareas={tareas} onRefresh={loadExpediente} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* NOTAS */}
        <AccordionItem value="notas" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">📄</div>
              Notas
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div ref={notasRef}>
              <NotasSection expedienteId={parseInt(id)} notas={notas} onRefresh={loadExpediente} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* EVENTOS */}
        <AccordionItem value="eventos" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white">📅</div>
              Eventos
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div ref={eventosRef}>
              <EventosSection expedienteId={parseInt(id)} onRefresh={loadExpediente} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ADJUNTOS */}
        <AccordionItem value="adjuntos" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">📎</div>
              Adjuntos
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <AdjuntosSection 
              entityType="expediente" 
              entityId={parseInt(id)} 
              showHeader={false}
            />
          </AccordionContent>
        </AccordionItem>

        {/* FACTURAS */}
        <AccordionItem value="facturas" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">💶</div>
              Facturas ({facturas.length})
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            {facturas.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No hay facturas asociadas</p>
            ) : (
              <div className="space-y-3">
                {facturas.map((factura) => {
                  const total = parseFloat(factura.total) || 0;
                  const pendiente = parseFloat(factura.total_pendiente) || 0;
                  const estadoCls = factura.estado === 'pagada'
                    ? 'bg-green-100 text-green-800'
                    : factura.estado === 'parcial'
                      ? 'bg-yellow-100 text-yellow-800'
                      : factura.estado === 'anulada'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-orange-100 text-orange-800';
                  return (
                  <Card
                    key={factura.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/contabilidad/factura/${factura.id}`)}
                    data-testid={`factura-card-${factura.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {factura.numero || factura.numero_factura || `#${factura.id}`}
                            {factura.tipo && factura.tipo !== 'factura' && (
                              <span className="ml-2 text-xs uppercase text-gray-500">[{factura.tipo}]</span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">{factura.concepto || factura.descripcion || '—'}</p>
                          {factura.fecha_emision && (
                            <p className="text-xs text-gray-500 mt-1">{new Date(factura.fecha_emision).toLocaleDateString('es-ES')}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">{total.toFixed(2)}€</p>
                          {pendiente > 0 && pendiente !== total && (
                            <p className="text-xs text-orange-600">Pendiente: {pendiente.toFixed(2)}€</p>
                          )}
                          <Badge className={estadoCls}>{factura.estado}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Floating Action Button */}
      <FloatingActionButton onAction={handleFABAction} />
    </div>
  );
};

export default ExpedienteDetalle;